import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import ICO_PAY_CARD from 'assets/images/icons/pay-black.svg';
import ICO_USERS from 'assets/images/icons/user-group-black.svg';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import LINK from 'constants/link.const';
import useIsFromMobileApp from 'hooks/useIsFromMobileApp.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useSalonBasicInfo } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectOldFlagsHideStaffManagement } from 'selectors/oldFlags.selectors';
import linkTo from 'utils/links/linkTo.util';

import CN from './SettingsLinksGroup.module.scss';

const SettingsLinksGroup = ({ hideStaffManagement }) => {
  const { isDarkMode } = useTheme();
  const { salonId } = useSalonBasicInfo();
  const isFromMobile = useIsFromMobileApp();
  const { t } = useTranslation();

  const roles = linkTo({ pattern: LINK.settingRoles, params: { salonId }, fault: '#' });
  const services = linkTo({ pattern: LINK.services, params: { salonId }, fault: '#' });
  const stylist = linkTo({ pattern: LINK.settingStylist, params: { salonId }, fault: '#' });
  const permissions = linkTo({ pattern: LINK.permissions, params: { salonId }, fault: '#' });
  const hours = linkTo({ pattern: LINK.salonHours, params: { salonId }, fault: '#' });
  const settings = linkTo({ pattern: LINK.settingsCalendar, params: { id: salonId }, fault: '#' });
  const onlineBookings = linkTo({ pattern: LINK.salonOnline, params: { salonId }, fault: '#' });
  const billing = linkTo({ pattern: LINK.billing, params: { salonId }, fault: '#' });
  const userNetwork = linkTo({ pattern: LINK.settingsUserNetwork, params: { id: salonId }, fault: '#' });
  const userSettings = linkTo({ pattern: LINK.settingsUser, params: { id: salonId }, fault: '#' });

  const staffLinks = useMemo(
    () => [
      {
        key: 'roles',
        to: roles,
        icon: 'briefcase',
        name: t('staffAndBusiness.summary.staffSettings.link1Name'),
        description: t('staffAndBusiness.summary.staffSettings.link1Description'),
      },
      {
        key: 'services',
        to: services,
        icon: 'document',
        name: t('staffAndBusiness.summary.staffSettings.link2Name'),
        description: t('staffAndBusiness.summary.staffSettings.link2Description'),
      },
      {
        key: 'stylist',
        to: stylist,
        icon: 'userGroup',
        name: t('staffAndBusiness.summary.staffSettings.link3Name'),
        description: t('staffAndBusiness.summary.staffSettings.link3Description'),
        isConditional: true,
      },
      {
        key: 'permissions',
        to: permissions,
        icon: 'shield',
        name: t('staffAndBusiness.summary.staffSettings.link4Name'),
        description: t('staffAndBusiness.summary.staffSettings.link4Description'),
      },
    ],
    [roles, t, services, stylist, permissions],
  );

  const accountLinks = useMemo(
    () => [
      {
        key: 'hours',
        to: hours,
        icon: 'building',
        name: t('staffAndBusiness.summary.accountSettings.link1Name'),
        description: t('staffAndBusiness.summary.accountSettings.link1Description'),
      },
      {
        key: 'settings',
        to: settings,
        icon: 'calendarEmpty',
        name: t('staffAndBusiness.summary.accountSettings.link2Name'),
        description: t('staffAndBusiness.summary.accountSettings.link2Description'),
      },
      {
        key: 'onlineBookings',
        to: onlineBookings,
        icon: 'arrowCursor',
        name: t('staffAndBusiness.summary.accountSettings.link3Name'),
        description: t('staffAndBusiness.summary.accountSettings.link3Description'),
      },
      {
        key: 'billing',
        to: billing,
        icon: 'pay', //
        name: t('staffAndBusiness.summary.accountSettings.link4Name'),
        description: t('staffAndBusiness.summary.accountSettings.link4Description'),
      },
      {
        key: 'userNetwork',
        to: userNetwork,
        icon: 'computer',
        name: t('staffAndBusiness.summary.accountSettings.link5Name'),
        description: t('staffAndBusiness.summary.accountSettings.link5Description'),
      },
      {
        key: 'userSettings',
        to: userSettings,
        icon: 'padlock',
        name: t('staffAndBusiness.summary.accountSettings.link6Name'),
        description: t('staffAndBusiness.summary.accountSettings.link6Description'),
      },
    ],
    [hours, t, settings, onlineBookings, billing, userNetwork, userSettings],
  );

  return (
    <div
      data-bem="SettingsLinksGroup"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.title}>
        <img className={CN.icoTitle} src={ICO_USERS} alt="" />
        {t('staffAndBusiness.summary.staffSettings.title')}
      </div>
      <div
        className={cn({
          [CN.containerLinks]: true,
          [CN.isSingleBox]: true,
        })}
      >
        {staffLinks.map((link) => {
          if (link.isConditional) {
            return (
              <div key={link.key} className={CN.navBox}>
                {hideStaffManagement ? (
                  <Tooltip variant="top" content={t('staffAndBusiness.summary.tooltipText')}>
                    <div className={CN.flex}>
                      <div className={CN.linkTitleContainer}>
                        <Icon variant={link.icon} width="lg" />
                        <div className={CN.linkName}>{link.name}</div>
                      </div>
                      <Icon variant="circleWithArrowLeft" width="xl" />
                    </div>
                    <div className={CN.linkDescription}>{link.description}</div>
                  </Tooltip>
                ) : (
                  <NavLink to={link.to} className={CN.linkSettings}>
                    <div className={CN.flex}>
                      <div className={CN.linkTitleContainer}>
                        <Icon variant={link.icon} width="lg" />
                        <div className={CN.linkName}>{link.name}</div>
                      </div>
                      <Icon variant="circleWithArrowLeft" width="xl" />
                    </div>
                    <div className={CN.linkDescription}>{link.description}</div>
                  </NavLink>
                )}
              </div>
            );
          }

          return (
            <NavLink key={link.key} to={link.to} className={CN.navBox}>
              <div className={CN.flex}>
                <div className={CN.linkTitleContainer}>
                  <Icon variant={link.icon} width="lg" />
                  <div className={CN.linkName}>{link.name}</div>
                </div>
                <Icon variant="circleWithArrowLeft" width="xl" />
              </div>
              <div className={CN.linkDescription}>{link.description}</div>
            </NavLink>
          );
        })}
      </div>

      <div className={CN.title}>
        <img className={CN.icoTitle} src={ICO_PAY_CARD} alt="" />
        {t('staffAndBusiness.summary.accountSettings.title')}
      </div>
      <div
        className={cn({
          [CN.containerLinks]: true,
          [CN.isSingleBox]: true,
        })}
      >
        {accountLinks
          .filter((link) => !(isFromMobile && link.key === 'billing')) // hidden billing for mobile app
          .map((link) => (
            <NavLink key={link.key} to={link.to} className={CN.navBox}>
              <div className={CN.flex}>
                <div className={CN.linkTitleContainer}>
                  <Icon variant={link.icon} width="lg" />
                  <div className={CN.linkName}>{link.name}</div>
                </div>
                <Icon variant="circleWithArrowLeft" width="xl" />
              </div>
              <div className={CN.linkDescription}>{link.description}</div>
            </NavLink>
          ))}
      </div>
    </div>
  );
};

const SettingsLinksGroupHoc = connect((state) => ({
  hideStaffManagement: selectOldFlagsHideStaffManagement(state),
}))(SettingsLinksGroup);

export default SettingsLinksGroupHoc;
